/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {BaseComponent, ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {DB_User, getUserNameWithTitle} from "@app/ir-q-app-common/types/care-center/user";
import {OnPatientDataUpdatedForMany, PatientsDataModule} from "@modules/PatientDataModule";
import {padding_rl_med} from "@styles/styles";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {DateTimesUtil} from "../patients/DateTimesUtil";
import {UsersModule} from "@modules/UsersModule";
import {tableCardStyle} from "../../home/AlertsTab";
import {TS_Table} from "../../../_TS_TABLE";
import {DB_Patient} from "@app/app-shared/patient";
import {hideTooltip, showTooltip} from "../../widget/appTooltip";
import {showAddPatientDialog} from "../patients/AddPatientDialog";
import {ICONS} from "@res/icons";
import {showDeleteUserDialog} from "../patients/DeleteUserDialog";
import {SearchBar} from "../SearchBar";
import {currentTimeMillies, Day} from "@intuitionrobotics/ts-common";
import {SelectOrganization} from "../../widget/SelectOrganization";
import {Loader} from "../../../../widgets/Loader"
import {TimeFilterOption, TimeFilterOptions, UserPreferencesModule} from "@modules/UserPreferencesModule";
import {CalendarDialog, TimeAndPretty} from "../messages/CalendarDialog";
import {OKButton} from "../../widget/OKButton";
import {AppPermissions} from "../../../utils/Permissions";
import {AppRoleLevel, ID_ALL} from "@app/app-shared/permission";
import {PatientsContext, PatientsContextType} from "../patients/PatientsContext";
import {OrganizationMap, OrganizationsContext} from "../organizations/OrganizationsContext";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {ContactsContext, ContactsMap} from "../messages/ContactsContext";

type TableRow = {
    patientName: DB_Patient
    unitName: string
    organization: string
    timezone: string
    careManager?: DB_User
    caseManager?: DB_User
    physician?: DB_User
    actions: DB_Patient
    birthday?: number
}

const patientTableWrapper = emotion.css(
    {
        padding: 20,
        boxSizing: "border-box"
    }
);

type State = {
    searchText: string
    selectedTimePeriod: TimeFilterOption
    downloadingExcel: boolean
    filterDate?: {
        start: TimeAndPretty
        end: TimeAndPretty
    }
    openCalender: boolean
    minDate: Date
    orgFilter: Set<string>
};

export class PatientsAdminTab
    extends BaseComponent<{}, State> implements OnPatientDataUpdatedForMany {
    private prefixExcelName: string = "";

    constructor(props: {}) {
        super(props);
        const timeFilterOptions = UserPreferencesModule.getTimeFilterOptions();
        this.state = {
            searchText: "",
            downloadingExcel: false,
            selectedTimePeriod: timeFilterOptions.find(
                item => item.option === TimeFilterOptions.CHOOSE_DAYS) || timeFilterOptions[0],
            filterDate: undefined,
            openCalender: false,
            minDate: new Date(2023, 10, 1), // November 1st, 2023
            orgFilter: new Set<string>()
        };
    }

    __onPatientDataUpdatedForMany = () => {
        this.setState({downloadingExcel: false});
    };

    clearTimestampFilter = () => {
        this.setState({filterDate: undefined});
    };

    renderDateFilter = () => {
        const date_placeholder = "mm/dd/yy";
        return <div className={"ll_v_s"}>
            <div className={"ll_h_c"} style={{display: "contents"}}>
                <div style={{
                    border: `1px solid ${COLORS.neutral_gray_2}`,
                    borderRadius: 2,
                    width: 190
                }}>
                    <div style={{position: "relative"}}>
                        <div className={"clickable"} onClick={() => this.setState(state => ({openCalender: !state.openCalender}))}>
                            {FONTS.reg_14(`${this.state.filterDate?.start.name || date_placeholder} - ${this.state.filterDate?.end.name || date_placeholder}`,
                                {
                                    color: this.state.filterDate?.start.name ? COLORS.neutral_text_dark : COLORS.neutral_gray_2,
                                    padding: "5px 10px",
                                    whiteSpace: "nowrap"
                                })}
                        </div>
                        {
                            this.state.openCalender &&
                            <>
                                <div className={"full_screen"} onClick={() => this.setState({openCalender: false})}/>
                                <div style={{position: "fixed", transform: "translate(0%)", marginTop: 10, zIndex: 15}}>
                                    <CalendarDialog
                                        onFilter={(startDate, endDate) => {
                                            this.setState({filterDate: {start: startDate, end: endDate}, openCalender: false});
                                        }}
                                        onClear={this.clearTimestampFilter}
                                        filterDate={this.state.filterDate}
                                        minDate={this.state.minDate}
                                    />
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>

        </div>;
    };

    getAdminContent = (contacts: ContactsMap, users: DB_User[], {patientIds, patients}: PatientsContextType): TableRow[] => {
        if (!patientIds.length)
            return [];

        return patientIds.reduce((acc: TableRow[], patientId) => {
            const _patient = patients[patientId];
            if (!_patient)
                return acc;

            if (this.state.orgFilter.size && !this.state.orgFilter.has(_patient.organizationId))
                return acc;

            const matchesName = _patient.name.toLowerCase().includes(this.state.searchText.toLowerCase());
            const matchesUnitId = _patient.unitId.toLowerCase().includes(this.state.searchText.toLowerCase());
            if (!matchesUnitId && !matchesName)
                return acc;

            return [...acc, {
                patientName: _patient,
                unitName: _patient.unitId,
                organization: _patient.organizationId,
                timezone: _patient.timezone,
                careManager: users.find(_user => _user._id === _patient.careManagerId),
                caseManager: users.find(_user => _user._id === _patient.caseManagerId),
                physician: users.find(_user => _user._id === _patient.physicianId),
                actions: _patient,
                birthday: contacts[_patient.contactId]?.birthday
            }]
        }, []);
    };

    private header: (keyof TableRow)[] = ["patientName",
        "unitName",
        "organization",
        "timezone",
        "birthday",
        "physician",
        "careManager",
        "caseManager",
        "actions"];

    headerStyle = (title?: string, style?: CSSProperties) => {
        return <div className={`ll_h_c ${padding_rl_med}`}
                    style={{...style, height: 34, backgroundColor: COLORS.color_gray_medium}}>
            {title && FONTS.reg_14_secondary_dark(title, {whiteSpace: "nowrap"})}
        </div>;
    };

    rowStyle = (content?: string | number) => {
        const textStyle = {color: COLORS.neutral_text_dark, lineHeight: "24px"}; // line height should be at least half of row height for maximum of 2 lines
        const blockWithText = emotion.css({maxHeight: "47px"});

        return <div
            className={`ll_h_c ${padding_rl_med}`}
            style={{height: 47, cursor: "pointer"}}>
            <div className={blockWithText}>
                {content && FONTS.reg_16(`${content}`, textStyle)}
            </div>
        </div>;
    };


    toolTipContent = () => {
        return <div style={{backgroundColor: COLORS.white}}>
        </div>;
    };

    renderPatientsTable = (contacts: ContactsMap, orgMap: OrganizationMap, organizations: DB_Organization[], pctx: PatientsContextType) => {
        const users = UsersModule.getUsers();
        return <div style={{...tableCardStyle, minHeight: "80%"}}>
            {this.state.downloadingExcel && <Loader/>}
            <TS_Table<TableRow>
                id={"admin-table"}
                header={this.header}
                rows={this.getAdminContent(contacts, users, pctx)}
                td={{
                    style: {borderBottom: `1px solid ${COLORS.grays_2}`}
                }}
                style={{width: "100%"}}
                headerRenderer={{
                    patientName: () => this.headerStyle("Customer"),
                    unitName: () => this.headerStyle("Unit Name"),
                    organization: () => this.headerStyle("Organization"),
                    timezone: () => this.headerStyle("Timezone"),
                    birthday: () => this.headerStyle("Birthday"),
                    careManager: () => this.headerStyle("Care Manager"),
                    caseManager: () => this.headerStyle("Case Manager"),
                    physician: () => this.headerStyle("Physician"),
                    actions: () => this.headerStyle("Actions", {justifyContent: "center"})
                }}
                cellRenderer={{
                    patientName: (cellValue: DB_Patient) => <div>
                        <div style={{
                            alignSelf: "flex-start",
                            height: "35px"
                        }} className="ll_h_c"
                             onMouseEnter={(e) => showTooltip(e, this.toolTipContent())}
                             onMouseLeave={() => hideTooltip()}>
                            {this.rowStyle(cellValue.name)}</div>
                    </div>,
                    unitName: (unit: string) => this.rowStyle(unit),
                    organization: (org: string) => this.rowStyle(orgMap[org]?.name),
                    timezone: (timezone: string) => this.rowStyle(timezone),
                    birthday: (bd?: number) => this.rowStyle(bd ? DateTimesUtil.birthdayPresentation(bd) : 'N/A'),
                    careManager: (careManager?: DB_User) =>
                        <div>{careManager && this.rowStyle(getUserNameWithTitle(careManager))}</div>,
                    caseManager: (caseManager?: DB_User) =>
                        <div>{caseManager && this.rowStyle(getUserNameWithTitle(caseManager))}</div>,
                    physician: (physician?: DB_User) =>
                        <div>{physician && this.rowStyle(getUserNameWithTitle(physician))}</div>,
                    actions: (patient: DB_Patient) => <div
                        className={"ll_h_c"}
                        style={{justifyContent: "space-around"}}
                    >
                        <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                            <div key={"edit"}
                                 className={"ll_h_c"}
                                 onClick={() => {
                                     if (!organizations.length)
                                         return;

                                     showAddPatientDialog(organizations, patient);
                                 }}
                                 style={{cursor: "pointer"}}>
                                {ICONS.icon_filters(COLORS.grays_dark, 20)}
                            </div>
                        </AppPermissions>
                        <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                            <div key={"delete"}
                                 className={"ll_h_c"}
                                 onClick={() => showDeleteUserDialog(patient)}
                                 style={{cursor: "pointer"}}>
                                {ICONS.close(COLORS.red, 18)}
                            </div>
                        </AppPermissions>
                        <div key={"downloadFile"}
                             className={"ll_h_c"}
                             onClick={() => {
                                 this.setState({downloadingExcel: true})
                                 let daysAgo;
                                 if (this.state.selectedTimePeriod.option === TimeFilterOptions.CHOOSE_DAYS) {
                                     this.prefixExcelName = "30 days";
                                     daysAgo = 30;
                                 } else {
                                     this.prefixExcelName = this.state.selectedTimePeriod.value;
                                     daysAgo = this.state.selectedTimePeriod.days;
                                 }
                                 const orgId = patient.organizationId;
                                 PatientsDataModule.getAllPatientDataForMany(contacts, [orgMap[orgId]], this.prefixExcelName, [patient.unitId], currentTimeMillies() - (daysAgo * Day));
                             }}
                             style={{cursor: "pointer"}}>
                            {ICONS.icon_download(COLORS.grays_dark, 16)}
                        </div>
                    </div>
                }}
            />
        </div>
    };

    render() {
        return <PatientsContext.Consumer>{
            (pctx) => {
                return <OrganizationsContext.Consumer>
                    {orgCtx => {
                        return <ContactsContext.Consumer>
                            {({contacts}) => {
                                const organizations = Object.values(orgCtx.organizations);
                                return <div className={`match_width ${patientTableWrapper}`}>
                                    <div style={{display: "flex", alignItems: "center"}}>
                                        <SearchBar value={this.state.searchText} onValueChange={(value) => this.setState({searchText: value})}
                                                   onClose={() => this.setState({searchText: ""})}/>
                                        <SelectOrganization
                                            onOrgClicked={(orgId) => {
                                                if (orgId === ID_ALL)
                                                    return this.setState(s => ({...s, orgFilter: new Set()}));
                                                this.setState(s => {
                                                    const orgs = s.orgFilter;
                                                    if (orgs.has(orgId))
                                                        orgs.delete(orgId);
                                                    else
                                                        orgs.add(orgId);
                                                    return {...s, orgFilter: orgs};
                                                });
                                            }}
                                            selected={this.state.orgFilter}
                                        />
                                        {this.renderDateFilter()}
                                        {this.renderDownloadButton(contacts, orgCtx.organizations, organizations, pctx)}
                                        <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                                            <OKButton
                                                text={"Add Customer"}
                                                enabled={!!organizations.length}
                                                onClick={() => showAddPatientDialog(organizations)}
                                                wrapperStyle={{
                                                    marginLeft: 8,
                                                    minHeight: "unset",
                                                    minWidth: "unset"
                                                }}
                                                inverted
                                            />
                                        </AppPermissions>
                                    </div>
                                    {this.renderPatientsTable(contacts, orgCtx.organizations, organizations, pctx)}
                                </div>;

                            }}</ContactsContext.Consumer>
                    }}</OrganizationsContext.Consumer>
            }}</PatientsContext.Consumer>
    }

    private renderDownloadButton(contacts: ContactsMap, orgMap: OrganizationMap, orgs: DB_Organization[], {patients}: PatientsContextType) {
        if (!this.state.filterDate?.start)
            return;

        const filterDate = this.state.filterDate

        return <div style={{marginLeft: 8}} onClick={() => {
            const orgIds = orgs.length === 1 ? new Set([orgs[0]._id]) : this.state.orgFilter;

            if (!orgIds.size)
                return ToastModule.toastError(FONTS.medium_16_white("Please select organization first"));

            if (filterDate?.start?.timestamp < 1698789600000)
                return ToastModule.toastError(FONTS.medium_16_white("Data is not available before Nov 1st, 2023"));

            const unitIds = Object.values(patients).reduce((acc: string[], patient) => {
                if (patient && orgIds.has(patient.organizationId))
                    acc.push(patient.unitId);
                return acc;
            }, [])
            if (!unitIds.length)
                return ToastModule.toastError(FONTS.medium_16_white("No patients found for selected organization"));
            this.setState({downloadingExcel: true});
            PatientsDataModule.getAllPatientDataForMany(contacts, [...orgIds].map(oId => orgMap[oId]), `${filterDate?.start.name}_${filterDate?.end.name}`, unitIds, filterDate?.start.timestamp, filterDate?.end.timestamp);
        }}>
            {ICONS.icon_download(COLORS.grays_dark, 16)}
        </div>;
    }


}
